<template>
  <b-modal :id="id" @hidden="clean" @ok="confirm" :title="title" ok-title="Выполнить" header-bg-variant="success" header-text-variant="light" ok-variant="success" size="lg">
    <template #modal-title v-if="selectedTool">
      <b-link :href="selectedTool.reference" target="_blank">
        <b-img v-if="selectedTool.icon" :src="selectedTool.icon" width="32"/>
        <b-img v-if="!selectedTool.icon" src="favicon.ico" width="32"/>
      </b-link>
      {{ title }}
    </template>
    <b-form ref="execute_form">
      <b-tabs fill card active-nav-item-class="text-success">
        <b-tab title-link-class="text-secondary">
          <template #title>
            <b-icon icon="play-fill"/> Основное
          </template>
          <b-form-group description="Проект" invalid-feedback="Проект обязателен" v-if="!project && !target">
            <b-form-select v-model="projectId" :options="projects" @change="selectProject" value-field="id" text-field="name" :state="projectState">
              <template #first>
                <b-form-select-option :value="null" disabled>Выберите проект</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
          <b-form-group description="Цели" invalid-feedback="Цели обязательны" v-if="!target">
            <b-form-select multiple :select-size="3" v-model="targetIds" :options="targets" :disabled="!selectedProject" value-field="id" text-field="target" :state="targetState"/>
            <b-form-checkbox switch @change="selectAllTargets" size="md">Выбрать все</b-form-checkbox>
          </b-form-group>
          <b-form-group description="Процесс" v-if="!process && !tool">
            <b-form-select v-model="processId" :options="processes" @change="selectProcess" value-field="id" text-field="name" :state="processState"/>
          </b-form-group>
          <b-form-group description="Инструмент" v-if="!process && !tool">
            <b-form-select v-model="toolId" :options="tools" @change="selectTool" value-field="id" text-field="name" :state="toolState"/>
          </b-form-group>
          <b-form-group description="Конфигурация инструмента" v-if="!process">
            <b-form-select v-model="configurationId" :options="selectedTool ? selectedTool.configurations : []" :disabled="!configurationId" @change="selectConfiguration" value-field="id" text-field="name"/>
          </b-form-group>
          <b-form-group description="Интенсивность выполнения">
            <b-input-group>
              <b-input-group-prepend>
                <div v-for="i in intensitySelection" :key="i.value">
                  <b-button v-if="i.intensity_rank === intensity" v-b-tooltip.hover.top="i.intensity_rank" :variant="i.variant" no-remove>{{ intensity ? intensity.charAt(0) : 'H' }}</b-button>
                </div>
              </b-input-group-prepend>
              <b-form-select v-model="intensity" :options="intensitySelection" value-field="intensity_rank" text-field="intensity_rank" required/>
            </b-input-group>
          </b-form-group>
          <b-form-group v-if="agents.length > 0" label="Агент выполнения" description="Где выполнять задачу: локально или на агенте">
            <b-form-select v-model="agentId" :options="agentOptions" value-field="value" text-field="text"/>
            <small v-if="agentId" class="text-muted">Задача будет ожидать выбранного агента в его очереди</small>
            <b-form-text v-if="agentId && selectedAgentOffline" class="text-warning">⚠ Внимание: выбранный агент не в сети. Задача будет ожидать его подключения.</b-form-text>
          </b-form-group>
        </b-tab>
        <b-tab title-link-class="text-secondary" v-if="checkInputType('Wordlist')">
          <template #title>
            <b-icon icon="file-earmark-word-fill"/> Словари
          </template>
          <b-form-group description="Выберите словари для использования">
            <b-form-select v-model="wordlistsItems" multiple :select-size="10">
              <option v-for="wordlist in wordlists" :key="wordlist.id" :value="wordlist.id">{{ wordlist.name }} - {{ wordlist.type }}</option>
            </b-form-select>
          </b-form-group>
        </b-tab>
        <b-tab title-link-class="text-secondary">
          <template #title>
            <b-icon icon="clock-fill"/> Расписание
          </template>
          <label>Выполнить в указанное время</label>
          <b-form-group>
            <b-form-datepicker v-model="scheduledAtDate" @input="cleanScheduledIn" :state="scheduledAtState" :min="minimumDate" today-button reset-button close-button/>
          </b-form-group>
          <b-form-group invalid-feedback="Время обязательно и должно быть в будущем">
            <b-form-timepicker v-model="scheduledAtTime" @input="cleanScheduledIn" :state="scheduledAtState"/>
          </b-form-group>
          <hr/>
          <label>Выполнить через некоторое время</label>
          <b-form-group description="Значение времени">
            <b-input-group :prepend="scheduledIn">
              <b-form-input v-model="scheduledIn" type="range" min="1" max="100" @change="cleanScheduledAt"/>
            </b-input-group>
          </b-form-group>
          <b-form-group description="Единица времени">
            <b-form-select v-model="scheduledTimeUnit" :options="timeUnits" value-field="value" text-field="text"/>
          </b-form-group>
        </b-tab>
        <b-tab title-link-class="text-secondary">
          <template #title>
            <b-icon icon="arrow-clockwise"/> Повтор
          </template>
          <label>Повторять выполнение периодически</label>
          <b-form-group description="Значение времени">
            <b-input-group :prepend="repeatIn">
              <b-form-input v-model="repeatIn" type="range" min="1" max="100"/>
            </b-input-group>
          </b-form-group>
          <b-form-group description="Единица времени">
            <b-form-select v-model="repeatTimeUnit" :options="timeUnits" value-field="value" text-field="text"/>
          </b-form-group>
        </b-tab>
      </b-tabs>
    </b-form>
    <b-progress v-if="processed > 0 && targetIds.length > 1" :value="processed" :max="targetIds.length" variant="success" show-value/>
  </b-modal>
</template>

<script>
import RekonoApi from '@/backend/RekonoApi'
export default {
  name: 'taskModal',
  mixins: [RekonoApi],
  props: {
    id: String,
    process: Object,
    tool: Object,
    project: Object,
    target: Object,
    reload: {
      type: Boolean,
      default: false
    },
    initialized: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    title () {
      if (this.process) {
        return `Выполнить ${this.process.name}`
      } else if (this.tool) {
        return `Выполнить ${this.tool.name}`
      }
      return 'Новая задача'
    },
    intensitySelection () {
      if (this.selectedTool) {
        return this.intensityByVariant.filter(intensity => this.selectedTool.intensities.find(i => i.intensity_rank === intensity.intensity_rank))
      }
      return this.intensityByVariant
    },
    selectedAgentOffline () {
      if (!this.agentId) return false
      const agent = this.agents.find(a => a.id === this.agentId)
      return agent && agent.status !== 'online'
    },
    agentOptions () {
      const opts = [{ value: null, text: 'Локально (на сервере)' }]
      // Фильтруем агентов по проекту если выбран
      let projectAgents = this.agents
      if (this.selectedProject && this.selectedProject.agents) {
        const projectAgentIds = this.selectedProject.agents.map(a => a.id)
        projectAgents = this.agents.filter(a => projectAgentIds.includes(a.id))
      }
      projectAgents.forEach(a => {
        opts.push({
          value: a.id,
          text: `${a.name}${a.hostname ? ' (' + a.hostname + ')' : ''} — ${a.status === 'online' ? 'в сети' : 'не в сети'}`,
        })
      })
      return opts
    }
  },
  data () {
    return {
      isWordlist: false,
      projects: [],
      targets: [],
      processes: [],
      tools: [],
      wordlists: [],
      agents: [],
      selectedProject: null,
      selectedProcess: null,
      selectedTool: null,
      selectedConfiguration: null,
      projectId: null,
      targetIds: [],
      processId: null,
      toolId: null,
      configurationId: null,
      agentId: null,
      intensity: 'Normal',
      wordlistsItems: [],
      scheduledAtDate: null,
      scheduledAtTime: null,
      scheduledIn: null,
      scheduledTimeUnit: 'Minutes',
      repeatIn: null,
      repeatTimeUnit: 'Days',
      projectState: null,
      targetState: null,
      processState: null,
      toolState: null,
      scheduledAtState: null,
      minimumDate: new Date(),
      processed: 0
    }
  },
  watch: {
    initialized (initialized) {
      if (initialized) {
        if (this.tool) {
          this.selectTool(this.tool.id, this.tool)
        } else if (this.process) {
          this.selectProcess(this.process.id, this.process)
        } else if (!this.tool && !this.process) {
          this.getAllPages('/api/tools/', { o: 'stage,name'}).then(results => this.tools = results)
          this.getAllPages('/api/processes/', { o: 'name' }).then(results => this.processes = results)
        }
        if (this.target) {
          this.targetIds = [this.target.id]
          this.get(`/api/projects/${this.target.project}/`).then(response => { this.selectProject(this.target.project, response.data) })
        } else if (this.project) {
          this.get(`/api/projects/${this.project.id}/`).then(response => { this.selectProject(this.project.id, response.data) })
        } else {
          this.getAllPages('/api/projects/', { o: 'name' }).then(results => this.projects = results)
        }
      }
    }
  },
  created () {
    this.getAllPages('/api/agents/', { o: 'name' })
      .then(results => { this.agents = results })
      .catch(() => { this.agents = [] })
  },
  methods: {
    check () {
      const valid = this.$refs.execute_form.checkValidity()
      this.projectState = (this.projectId && this.projectId > 0)
      this.targetState = this.targetIds.length > 0
      if (!this.processId && !this.toolId) {
        this.processState = false
        this.toolState = false
        return false
      }      
      if (this.scheduledAtDate || this.scheduledAtTime) {
        this.scheduledAtState = false
        if (this.scheduledAtDate && this.scheduledAtTime) {
          this.scheduledAtState = (Date.parse(`${this.scheduledAtDate} ${this.scheduledAtTime}`) > new Date())
        }
        return valid && this.scheduledAtState !== false
      }
      return valid && this.projectState && this.targetState
    },
    confirm (event) {
      event.preventDefault()
      if (this.check()) {
        this.create()
      }
    },
    create () {
      const data = {
        intensity_rank: this.intensity,
        scheduled_at: this.scheduledAtDate && this.scheduledAtTime ? new Date(`${this.scheduledAtDate}T${this.scheduledAtTime}`).toISOString() : null,
        scheduled_in: this.scheduledIn,
        scheduled_time_unit: this.scheduledTimeUnit,
        repeat_in: this.repeatIn,
        repeat_time_unit: this.repeatTimeUnit,
        wordlists: this.wordlistsItems
      }
      if (this.processId) {
        data.process_id = this.processId
      }
      if (this.toolId) {
        data.tool_id = this.toolId
      }
      if (this.configurationId) {
        data.configuration_id = this.configurationId
      }
      if (this.agentId) {
        data.agent_id = this.agentId
      }
      for (let index in this.targetIds) {
        data.target_id = this.targetIds[index]
        this.post('/api/tasks/', data, this.selectedTool ? this.selectedTool.name : this.selectedProcess.name, 'Выполнение запрошено успешно')
          .catch(task => { return Promise.resolve(task) })
          .then(task => {
            this.processed += 1
            if (this.processed === this.targetIds.length) {
              if (this.targetIds.length === 1) {
                this.$router.push({ name: 'task', params: { id: task.id, task: task }})
              }
              else if (this.$route.path === `/projects/${this.projectId}/tasks`) {
                this.$emit('confirm', { id: this.id, success: true, reload: true })
              }
              else {
                this.$router.push({ path: `/projects/${this.projectId}/tasks` })
              }
            }
          })
      } 
    },
    clean () {
      this.processes = []
      this.tools = []
      this.wordlists = []
      this.selectedProject = null
      this.selectedProcess = null
      this.selectedTool = null
      this.selectedConfiguration = null
      this.projectId = null
      this.targetIds = []
      this.processId = null
      this.toolId = null
      this.configurationId = null
      this.intensity = 'Normal'
      this.wordlistsItems = []
      this.scheduledAtDate = null
      this.scheduledAtTime = null
      this.scheduledIn = null
      this.scheduledTimeUnit = 'Minutes'
      this.repeatIn = null
      this.repeatTimeUnit = 'Days'
      this.projectState = null
      this.targetState = null
      this.scheduledAtState = null
      this.processed = 0
      this.$emit('clean')
    },
    checkInputType (inputType) {
      let inputs = []
      if (this.selectedTool) {
        this.selectedTool.arguments.map(argument => argument.inputs.map(input => inputs.push(input.type.name)))
      } else if (this.selectedProcess) {
        this.selectedProcess.steps.map(step => step.tool.arguments.map(argument => argument.inputs.map(input => inputs.push(input.type.name))))
      }
      return inputs.includes(inputType)
    },
    selectProject (projectId, project = null) {
      this.projectId = projectId
      if (project) {
        this.selectedProject = project
      } else {
        this.selectedProject = this.projects.find(p => p.id === projectId)
      }
      this.targets = this.selectedProject.targets
    },
    selectProcess (processId, process = null) {
      this.processId = processId
      this.toolId = null
      this.selectedTool = null
      this.configurationId = null
      this.selectedConfiguration = null
      this.intensity = 'Normal'
      if (process) {
        this.selectedProcess = process
      } else {
        this.selectedProcess = this.processes.find(p => p.id === processId)
      }
      const isWordlist = this.checkInputType('Wordlist')
      if (!this.isWordlist && isWordlist) {
        this.isWordlist = isWordlist
        this.updateWordlists()
      }
    },
    selectTool (toolId, tool = null) {
      this.processId = null
      this.selectedProcess = null
      this.toolId = toolId
      if (tool) {
        this.selectedTool = tool
      } else {
        this.selectedTool = this.tools.find(t => t.id === toolId)
      }
      this.selectConfiguration(this.selectedTool.configurations[0].id, this.selectedTool.configurations[0])
      const normalIntensity = this.selectedTool.intensities.find(i => i.intensity_rank === 'Normal')
      if (normalIntensity) {
        this.intensity = 'Normal'
      } else {
        this.intensity = this.selectedTool.intensities[this.selectedTool.intensities.length - 1].intensity_rank
      }
      const isWordlist = this.checkInputType('Wordlist')
      if (!this.isWordlist && isWordlist) {
        this.isWordlist = isWordlist
        this.updateWordlists()
      }
    },
    selectConfiguration (configurationId, configuration = null) {
      this.configurationId = configurationId
      if (configuration) {
        this.selectedConfiguration = configuration
      } else {
        this.selectedConfiguration = this.selectedTool.configurations.find(c => c.id === configurationId)
      }
    },
    selectAllTargets (checked) {
      if (checked) {
        this.targetIds = this.targets.map(target => target.id)
      }
      else {
        this.targetIds = []
      }
    },
    updateWordlists () {
      this.getAllPages('/api/wordlists/', { o: 'type,name' }).then(results => this.wordlists = results)
    },
    cleanScheduledIn () {
      this.scheduledIn = null
    },
    cleanScheduledAt () {
      this.scheduledAtDate = null
      this.scheduledAtTime = null
    }
  }
}
</script>

<style lang="scss" scoped>
$component-active-bg: black;
$custom-range-thumb-bg: green;
@import 'bootstrap/scss/bootstrap';
</style>
